import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { UserCog, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Single-lead assignment card for the Lead detail page (super_admin/gestor/admin only)
export const LeadAssignPanel = ({ leadId, userRole, currentAssignedName, currentAssignedId, isConverted, onAssigned }) => {
  const [users, setUsers] = useState([]);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const canAssign = ['super_admin', 'gestor', 'admin'].includes(userRole);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/leads/assignable-users`);
      setUsers((res.data || []).filter((u) => ['comercial', 'chefe_equipa'].includes(u.role)));
    } catch { /* silent */ }
  }, []);

  useEffect(() => { if (canAssign) load(); }, [canAssign, load]);
  useEffect(() => { setValue(currentAssignedId || ''); }, [currentAssignedId]);

  if (!canAssign) return null;

  const save = async () => {
    if (!value) { toast.error('Escolhe um comercial'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/leads/${leadId}/assign`, { assigned_to: value });
      toast.success(res.data?.message || 'Lead atribuída');
      onAssigned && onAssigned(res.data?.lead);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao atribuir');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded-2xl border-amber-100" data-testid="lead-assign-panel">
      <CardHeader className="border-b border-amber-100 pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2 text-amber-900">
          <UserCog className="w-4 h-4 text-amber-600" /> Atribuir Comercial
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {isConverted ? (
          <p className="text-xs text-slate-500">Lead já convertida — não pode ser reatribuída.</p>
        ) : (
          <>
            {currentAssignedName && <p className="text-xs text-slate-500">Atual: <span className="font-medium text-slate-700">{currentAssignedName}</span></p>}
            <Select value={value} onValueChange={setValue}>
              <SelectTrigger data-testid="assign-user-select"><SelectValue placeholder="Escolher comercial" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.role === 'chefe_equipa' ? ' (Chefe Equipa)' : ''}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={save} disabled={saving || !value || value === currentAssignedId} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="assign-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}Guardar Atribuição
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};
